import { ChainId, CurrencyAmount, MON, Token } from '@monadex/sdk'
import React, { useState, useCallback, useMemo } from 'react'
import { Box, Tooltip, CircularProgress, ListItemButton, Snackbar, Button, IconButton } from '@mui/material'
import { WrappedTokenInfo } from '@/state/list/hooks'
import { useAddUserToken, useRemoveUserAddedToken } from '@/state/user/hooks'
import { useIsUserAddedToken, _useCurrency } from '@/hooks/Tokens'
import { CurrencyLogo, PlusHelper, TokenWarningModal } from '@/components'
import { getTokenLogoURL } from '@/utils/getTokenLogoURL'
import { formatTokenAmount, shortAddr } from '@/utils/index'
import { getIsMetaMaskWallet } from '@/utils/connectors'
import { wrappedCurrency } from '@/utils/wrappedCurrency'
import { useWalletData } from '@/utils'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { Copy01Icon } from '@/utils/icons'
// TODO Investigate: shouldnt this key return  not 'MONAD'
function currencyKey (currency: Token): string {
  return currency instanceof Token
    ? currency.address
    : currency === MON
      ? 'MON'
      : ''
}

function Balance ({ balance }: { balance: CurrencyAmount }): JSX.Element {
  return (
    <p className='small' title={balance.toExact()}>
      {formatTokenAmount(balance)}
    </p>
  )
}

function TokenTags ({ currency }: { currency: Token }): JSX.Element {
  if (!(currency instanceof WrappedTokenInfo)) {
    return <span />
  }

  const tags = currency.tags
  if (tags === undefined || tags.length === 0) return <span />

  const tag = tags[0]
  return (
    <Box>
      <Tooltip title={tag.description}>
        <Box className='tag' key={tag.id}>
          {tag.name}
        </Box>
      </Tooltip>
      {tags.length > 1
        ? (
          <Tooltip
            title={tags
              .slice(1)
              .map(({ name, description }) => `${name}: ${description}`)
              .join('; \n')}
          >
            <Box className='tag'>...</Box>
          </Tooltip>
          )
        : null}
    </Box>
  )
}

interface CurrenyRowProps {
  currency: Token
  onSelect: () => void
  isSelected: boolean
  otherSelected: boolean
  style: any
  isOnSelectedList?: boolean
  balance: CurrencyAmount | undefined
}

const CurrencyRow: React.FC<CurrenyRowProps> = ({
  currency,
  onSelect,
  isSelected,
  otherSelected,
  style,
  isOnSelectedList,
  balance
}) => {
  const { account, chainId, provider } = useWalletData()
  const key = currencyKey(currency)
  const customAdded = useIsUserAddedToken(currency)
  const nativeCurrency = MON
  const useChain = chainId ?? ChainId.MONAD_TESTNET

  const removeToken = useRemoveUserAddedToken()
  const addToken = useAddUserToken()
  const isMetamask = getIsMetaMaskWallet() && isOnSelectedList

  // SnackBar notifiaction On copy
  const [open, setOpen] = useState(false)

  const handleCopy = () => {
    setOpen(true)
  }
  const handleClose = (event: any, reason: string) => {
    if (reason === 'clickaway') {
      return
    }

    setOpen(false)
  }

  // Always call these hooks, even if they might return null/undefined
  const [dismissTokenWarning, setDismissTokenWarning] = useState<boolean>(true)
  const addTokenToMetamask = (
    tokenAddress: string,
    tokenSymbol: string,
    tokenDecimals: number,
    tokenImage: any
  ): void => {
    if ((provider?.provider?.request) != null) {
      try {
        provider.provider.request({
          method: 'wallet_watchAsset',
          params: {
            type: 'ERC20',
            options: {
              address: tokenAddress,
              symbol: tokenSymbol,
              decimals: tokenDecimals,
              image: tokenImage
            }
          } as unknown as any[]
        })
      } catch (error) {
        console.error('Error adding token to MetaMask:', error)
      }
    }
  }

  const selectedToken = _useCurrency(
    wrappedCurrency(currency, useChain)?.address ?? undefined
  )

  const selectedTokens: Token[] = useMemo(
    () => [selectedToken]?.filter((c): c is Token => c instanceof Token) ?? [],
    [selectedToken]
  )

  const selectedTokensNotInDefault = selectedTokens

  const handleConfirmTokenWarning = useCallback(() => {
    setDismissTokenWarning(true)
  }, [])

  // reset if they close warning without tokens in params
  const handleDismissTokenWarning = useCallback(() => {
    setDismissTokenWarning(true)
  }, [])

  // only show add or remove buttons if not on selected list
  return (
    <>
      <TokenWarningModal
        isOpen={!dismissTokenWarning}
        tokens={selectedTokensNotInDefault}
        onConfirm={handleConfirmTokenWarning}
        onDismiss={handleDismissTokenWarning}
      />
      <ListItemButton
        style={style}
        key={key}
        id={`token-item-${key}`}
        selected={otherSelected || isSelected}
        onClick={() => {
          if (!isSelected && !otherSelected) onSelect()
        }}
      >
        <Box className='w-full flex p-4 items-center gap-3 no-scrollbar'>
          <CurrencyLogo currency={currency} size='32px' />
          <Box ml={1} height={32}>
            <Box className='flex items-center'>
              <small className='font-regular text-md'>{currency.symbol}</small>
              {isMetamask != null &&
                currency !== nativeCurrency &&
                !(currency.name === 'Monad') && (
                  <Box
                    ml='4px'
                    onClick={(event: any) => {
                      addTokenToMetamask(
                        currency.address,
                        currency.symbol ?? 'INVALID SYMBOL',
                        currency.decimals,
                        getTokenLogoURL(currency.address)
                      )
                      event.stopPropagation()
                    }}
                  >
                    <PlusHelper text='Add to metamask' />
                  </Box>
              )}
            </Box>
            {isOnSelectedList != null && isOnSelectedList
              ? (
                <Box className='flex items-center gap-2'>
                  {currency.name === 'Monad'
                    ? (
                      <span className='text-textSecondary'>MON</span>
                      )
                    : <span className='text-textSecondary font-fira'>{shortAddr(currency.address)}</span>}
                  {currency.address != null && (
                    <>
                      <CopyToClipboard
                        text={currency.address} onCopy={handleCopy}
                      >
                        <Copy01Icon width={19} />
                      </CopyToClipboard>

                      <Snackbar
                        open={open}
                        autoHideDuration={200}
                        message='Address Copied'
                        onClose={handleClose}
                        />
                    </>

                  )}
                </Box>
                )
              : (
                <Box className='flex items-center'>
                  <span className='text-sm font-semibold'>
                    {customAdded ? 'Added by user' : 'Found by address'}
                  </span>
                  <Box
                    ml={2}
                    className='text-xs font-medium bg-primary2 px-3 py-1 rounded-full '
                    onClick={(event) => {
                      event.stopPropagation()
                      if (customAdded) {
                        if (chainId != null && currency instanceof Token) { removeToken(chainId, currency.address) }
                      } else {
                        if (currency instanceof Token) {
                          addToken(currency)
                          setDismissTokenWarning(false)
                        }
                      }
                    }}
                  >
                    <span>
                      {customAdded ? 'Remove' : 'Add'}
                    </span>
                  </Box>
                </Box>
                )}
          </Box>

          <Box flex={1} />
          <TokenTags currency={currency} />
          <Box textAlign='right'>
            {balance
              ? (
                <>
                  <Balance balance={balance} />
                </>
                )
              : account
                ? (
                  <CircularProgress size={15} color='secondary' />
                  )
                : null}
          </Box>
        </Box>
      </ListItemButton>
    </>
  )
}

export default CurrencyRow
